import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface PublicSettings {
  publico: boolean;
  permite_anonimo: boolean;
  permite_multiplas_respostas: boolean;
  data_expiracao?: string | null;
  limite_respostas?: number | null;
}

export function useFormularioPublicSettings(formularioId: string) {
  const queryClient = useQueryClient();

  const updatePublicSettings = useMutation({
    mutationFn: async (settings: PublicSettings) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('formularios')
        .update({
          ...settings,
          atualizado_por: user?.id,
        })
        .eq('id', formularioId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formularios'] });
      toast.success("Configurações públicas atualizadas!");
    },
    onError: (error: any) => {
      console.error("Erro ao atualizar configurações:", error);
      toast.error(error.message || "Erro ao atualizar configurações");
    },
  });

  const regenerateToken = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Gerar novo UUID para o token
      const newToken = crypto.randomUUID();
      
      const { data, error } = await supabase
        .from('formularios')
        .update({
          token_publico: newToken,
          atualizado_por: user?.id,
        })
        .eq('id', formularioId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formularios'] });
      toast.success("Link público regenerado com sucesso!");
    },
    onError: (error: any) => {
      console.error("Erro ao regenerar token:", error);
      toast.error(error.message || "Erro ao regenerar link");
    },
  });

  return {
    updatePublicSettings: updatePublicSettings.mutate,
    regenerateToken: regenerateToken.mutate,
    isUpdating: updatePublicSettings.isPending,
    isRegenerating: regenerateToken.isPending,
  };
}
